import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { MyReportsRest } from './myreports.service';
import { TrackerRest } from '../../../../../plugins/tracker/services/tracker.rest';
import { TrackerService } from '../../../../../plugins/tracker/services/tracker.service';

@Injectable()
export class MyReportsFilterService {
  private projects: any = null;
  private baseFilters: any = {
    assigneesByTracker: {},
    statusesByTracker: {}
  };

  private fieldTypeToFilterGroup: { [key: string]: string } = {
    "1": "selects",
    "2": "selects",
    "3": "selects",
    "4": "text",
    "5": "selects",
    "6": "text",
    "7": "statuses",
    "8": "selects",
    "9": "dates",
    "10": "numerics",
    "11": "numerics",
    "12": "selects",
    "101": "text",
    "102": "text",
    "103": "assignees",
    "104": "priorities",
    "105": "dates",
    "106": "dates",
    "107": "dates",
    "108": "trackers",
    "110": "selects"
  };

  private fieldTypeMap: { [key: string]: string } = {
    '101': 'summary',
    '102': 'details',
    '103': 'assignees',
    '104': 'priority',
    '105': 'openDate',
    '106': 'closeDate',
    '107': 'lastModifiedDate'
  };

  private fieldTypeToReportFieldLabel: { [key: string]: string } = {
    '101': 'tracker_item.SUMMARY',
    '102': 'tracker_item.DETAILS',
    '103': 'tracker_item_assignee',
    '104': 'tracker_item.PRIORITY',
    '105': 'tracker_item.OPEN_DATE',
    '106': 'tracker_item.CLOSE_DATE',
    '107': 'tracker_item.LAST_MODIFIED_DATE',
    '108': 'tracker.TRACKER_NAME',
    '109': 'project.PROJECT_NAME',
    '110': 'tracker_item.WAITING_FOR'
  };

  constructor(
    private myReportsRest: MyReportsRest,
    private trackerRest: TrackerRest,
    private trackerService: TrackerService
  ) {}

  /**
   * Returns the default statuses.
   * @returns {any[]} Array of default status objects.
   */
  getDefaultStatuses(): any[] {
    return [
      { label: 'Open', id: '1', selected: false, enabled: true },
      { label: 'Closed', id: '0', selected: false, enabled: true }
    ];
  }

  /**
   * Returns the projects for the given unixName.
   * @param {string} unixName - The unixName to check.
   * @returns {Promise<any>} Promise resolving to the projects data.
   */
  getProjects(unixName: string): Promise<any> {
    if (!_.isEmpty(this.projects?.data) && _.get(this.projects, 'unixName', '') === unixName) {
      return Promise.resolve(this.projects.data);
    }
    return this.myReportsRest.getProjects().then((response: any) => {
      this.projects = {
        unixName: unixName,
        data: response
      };
      return this.projects.data;
    });
  }

  /**
   * Returns the base filters for the provided tracker IDs.
   * @param {any} trackerIds - Tracker IDs as an array.
   * @returns {Promise<any>} Promise resolving to the base filters.
   */
  getBaseFilters(trackerIds: any): Promise<any> {
    trackerIds = (Array.isArray(trackerIds)) ? trackerIds : [];
    const trackersToRequest = _.filter(trackerIds, (trackerId: any) => {
      return (_.get(this.baseFilters, 'assigneesByTracker.' + trackerId, null) == null) ? true : false;
    });
    if (trackersToRequest.length > 0) {
      const params = { trackers: _.join(trackersToRequest, ',') };
      return this.myReportsRest.getFilters(params).then((response: any) => {
        _.forOwn(response.assigneesByTracker, (item, key) => {
          this.baseFilters.assigneesByTracker[key] = item;
        });
        _.forOwn(response.statusesByTracker, (item, key) => {
          this.baseFilters.statusesByTracker[key] = item;
        });
        return this.baseFilters;
      });
    }
    return Promise.resolve(this.baseFilters);
  }

  /**
   * Returns the filter ID corresponding to a given filter type.
   * @param {any} filterType - The filter type.
   * @returns {any} The filter ID.
   */
  getFilterIdFromType(filterType: any): any {
    return _.get(this.fieldTypeMap, filterType, '');
  }

  /**
   * Creates and returns the basic filters object.
   * @param {any} options - Options for initializing filters.
   * @returns {any} The filters object.
   */
  init(options: any): any {
    const filters = {
      basic: {
        reportName: "",
        isShared: false,
        sharedUsers: "",
        openDateStart: "",
        openDateEnd: "",
        closeDateStart: "",
        closeDateEnd: ""
      },
      trackers: {
        display: true,
        count: 0,
        data: (options.trackers) ? this.formatTrackersByProject(options.trackers) : []
      },
      assignees: {
        display: false,
        count: 0,
        type: 103,
        data: []
      },
      statuses: {
        display: false,
        count: 0,
        data: []
      },
      priorities: {
        display: false,
        count: 0,
        type: 104,
        data: [
          { label: 'High', id: 1, selected: false },
          { label: 'Medium High', id: 2, selected: false },
          { label: 'Medium', id: 3, selected: false },
          { label: 'Low', id: 4, selected: false },
          { label: 'Very Low', id: 5, selected: false }
        ]
      },
      displayFields: {
        display: false,
        count: 0,
        basic: [
          { label: 'Summary', type: 101, selected: false },
          { label: 'Details', type: 102, selected: false },
          { label: 'Assignees', type: 103, selected: false },
          { label: 'Priority', type: 104, selected: false },
          { label: 'Open Date', type: 105, selected: false },
          { label: 'Close Date', type: 106, selected: false },
          { label: 'Last Modified Date', type: 107, selected: false },
          { label: 'Tracker Name', type: 108, selected: false },
          { label: 'Project Name', type: 109, selected: false }
        ],
        extra: []
      },
      text: {
        display: false,
        count: 0,
        basic: [
          { id: "summary", value: "", label: "Summary", type: 101 },
          { id: "description", value: "", label: "Description", type: 102 }
        ],
        extra: []
      },
      dates: {
        display: false,
        count: 0,
        basic: [
          { id: "lastmodifieddate", from: "", to: "", label: "Last Modified Date", type: 107 }
        ],
        extra: []
      },
      selects: {
        display: false,
        count: 0,
        basic: [],
        extra: []
      },
      numerics: {
        display: false,
        count: 0,
        extra: []
      }
    };

    this.setFilterAssignees(filters.assignees, options.assignees);
    this.setFilterElements(filters.statuses, options.statuses);
    return filters;
  }

  /**
   * Sets filter elements from the provided filter data.
   * @param {any} filter - The filter object to update.
   * @param {any} filterData - The filter data.
   */
  setFilterElements(filter: any, filterData: any): void {
    const elements = _.flatten(_.map(filterData, (elements, tid) => {
      return _.map(elements, (element) => ({ label: element, tracker: tid }));
    }));
    _.forEach(elements, (element) => {
      const filterElement = _.find(filter.data, { label: element.label });
      if (_.isEmpty(filterElement)) {
        filter.data.push({ label: element.label, enabled: false, selected: false, trackers: [element.tracker] });
      } else {
        filterElement.trackers = _.union(filterElement.trackers, [element.tracker]);
      }
    });
  }

  /**
   * Sets filter assignees from the provided assignee data.
   * @param {any} filter - The filter object to update.
   * @param {any} filterData - The assignee filter data.
   */
  setFilterAssignees(filter: any, filterData: any): void {
    const elements = _.flatten(_.map(filterData, (elements, tid) => {
      return _.map(elements, (element) => ({ id: element.id, label: element.label, tracker: tid }));
    }));
    _.forEach(elements, (element) => {
      const filterElement = _.find(filter.data, { id: element.id });
      if (_.isEmpty(filterElement)) {
        filter.data.push({ id: element.id, label: element.label, enabled: false, selected: false, trackers: [element.tracker] });
      } else {
        filterElement.trackers = _.union(filterElement.trackers, [element.tracker]);
      }
    });
  }

  /**
   * Enables filter elements that apply to any of the provided tracker IDs.
   * @param {any} filter - The filter array.
   * @param {any} trackerIds - The tracker IDs.
   */
  setFilterEnabledElements(filter: any, trackerIds: any): void {
    trackerIds = (!Array.isArray(trackerIds)) ? [] : trackerIds;
    _.forEach(filter, (filterItem) => {
      filterItem.enabled = !_.isEmpty(_.intersection(filterItem.trackers, trackerIds));
    });
  }

  /**
   * Formats the trackers by project.
   * @param {any} trackers - The trackers object.
   * @returns {any[]} Array of formatted projects with trackers.
   */
  formatTrackersByProject(trackers: any): any[] {
    return _.map(trackers, (item, project_id) => {
      const _trackers = _.map(item.trackers, (tracker, tracker_id) => {
        return { id: tracker_id, label: tracker, selected: false };
      });
      return { id: project_id, label: item.project_name, trackers: _trackers, selected: false };
    });
  }

  /**
   * Returns an array of selected tracker IDs from the trackersByProject object.
   * @param {any} trackersByProject - The trackers grouped by project.
   * @returns {any[]} Array of selected tracker IDs.
   */
  getSelectedTrackerIds(trackersByProject: any): any[] {
    return _.map(trackersByProject, (project) => {
      return _.filter(project.trackers, (tracker) => tracker.selected)
        .map((item) => item.id);
    }).flat();
  }

  /**
   * Returns the count of selected elements.
   * @param {any} filterElements - Array of filter elements.
   * @returns {number} Count of selected elements.
   */
  countSelectedElements(filterElements: any): number {
    return _.reduce(filterElements, (sum, item) => {
      return item.selected ? sum + 1 : sum;
    }, 0);
  }

  /**
   * Returns the count of used filters for a given section.
   * @param {any} filters - The filters object.
   * @param {string} section - The section (e.g., 'text', 'dates', 'numerics').
   * @returns {number} Count of used filters.
   */
  countUsedFilters(filters: any, section: string): number {
    return _.reduce(
      _.union(_.get(filters, section + '.basic', []), _.get(filters, section + '.extra', [])),
      (sum, item) => {
        if (_.get(item, 'value', '') !== '') { return sum + 1; }
        if (_.get(item, 'from', '') !== '') { return sum + 1; }
        if (_.get(item, 'to', '') !== '') { return sum + 1; }
        return sum;
      },
      0
    );
  }

  /**
   * Changes the order of a field within allFields.
   * @param {any} f - The field to move.
   * @param {any} allFields - The complete list of fields.
   * @param {boolean} up - If true, move the field up; otherwise, move it down.
   * @returns {any} The updated fields array.
   */
  changeFieldOrder(f: any, allFields: any, up: boolean): any {
    const isHigher = (item: any) => item.columnOrder > f.columnOrder;
    const isLower = (item: any) => item.columnOrder < f.columnOrder;
    const comparisonFn = up ? isLower : isHigher;
    const takeFn = up ? _.last : _.head;
    const target = takeFn(
      _.sortBy(
        _.filter(allFields, { selected: true })
          .filter((item) => item !== f)
          .filter(comparisonFn),
        'columnOrder'
      )
    );
    this.swapItemOrder(f, target);
    return this.updateFieldOrder(allFields);
  }

  /**
   * Updates the field order sequentially.
   * @param {any} fields - The array of fields.
   * @returns {any} The sorted fields array.
   */
  updateFieldOrder(fields: any): any {
    const sorted = _.sortBy(fields, 'columnOrder');
    for (let i = 0; i < sorted.length; i++) {
      sorted[i].columnOrder = i;
    }
    return sorted;
  }

  /**
   * Swaps the columnOrder values of two fields.
   * @param {any} f - The first field.
   * @param {any} target - The target field.
   */
  swapItemOrder(f: any, target: any): void {
    if (target) {
      const tmp = target.columnOrder;
      target.columnOrder = f.columnOrder;
      f.columnOrder = tmp;
    }
  }

  /**
   * Returns the display fields by merging basic and extra fields and updating their order.
   * @param {any} filters - The filters object.
   * @returns {any[]} Array of display field objects.
   */
  getDisplayFields(filters: any): any[] {
    const ret = _.union(_.get(filters, 'displayFields.basic', []), _.get(filters, 'displayFields.extra', []));
    return this.updateFieldOrder(ret);
  }

  /**
   * Returns the elements for a given group and optional subgroup.
   * @param {any} filters - The filters object.
   * @param {string} group_id - The group identifier.
   * @param {string} [subgroup_id] - The subgroup identifier.
   * @returns {any[]} Array of elements.
   */
  getElements(filters: any, group_id: string, subgroup_id?: string): any[] {
    switch (group_id) {
      case 'statuses':
      case 'assignees':
      case 'priorities':
        return _.get(filters, group_id + '.data', []);
      case 'displayFields':
        return this.getDisplayFields(filters);
      case 'selects':
        return _.get(
          _.find(
            _.concat(_.get(filters, 'selects.basic', []), _.get(filters, 'selects.extra', [])),
            { label: subgroup_id }
          ),
          'elements',
          []
        );
      default:
        console.log("getElements unknown case", group_id, subgroup_id);
        return [];
    }
  }

  /**
   * Returns a single element from the filters based on group, subgroup, and element ID.
   * @param {any} filters - The filters object.
   * @param {string} group_id - The group identifier.
   * @param {string} subgroup_id - The subgroup identifier.
   * @param {string} element_id - The element identifier.
   * @returns {any} The matching element.
   */
  getElement(filters: any, group_id: string, subgroup_id: string, element_id: string): any {
    const elements = this.getElements(filters, group_id, subgroup_id);
    switch (group_id) {
      case 'assignees':
      case 'priorities':
        return _.find(elements, { id: parseInt(element_id, 10) });
      default:
        return _.find(elements, (element: any) => element.label === element_id || element.value === element_id);
    }
  }

  /**
   * Toggles the selected status of an element and updates the total count.
   * @param {any} filters - The filters object.
   * @param {string} group_id - The group identifier.
   * @param {string} subgroup_id - The subgroup identifier.
   * @param {string} element_id - The element identifier.
   */
  toggleSelectedElement(filters: any, group_id: string, subgroup_id: string, element_id: string): void {
    const element = this.getElement(filters, group_id, subgroup_id, element_id);
    if (!element) {
      return;
    }
    element.selected = !element.selected;
    this.updateSelectedCount(filters, group_id);
  }

  /**
   * Updates the selected count for a filter group.
   * @param {any} filters - The filters object.
   * @param {string} group_id - The group identifier.
   */
  updateSelectedCount(filters: any, group_id: string): void {
    const fieldToSet = group_id + '.count';
    let newCount = 0;
    switch (group_id) {
      case 'statuses':
      case 'assignees':
      case 'priorities':
      case 'displayFields':
        newCount = this.countSelectedElements(this.getElements(filters, group_id));
        break;
      case 'selects':
        newCount = _.reduce(
          _.concat(_.get(filters, 'selects.basic', []), _.get(filters, 'selects.extra', [])),
          (sum, selectGroup) => this.countSelectedElements(selectGroup.elements) + sum,
          0
        );
        break;
    }
    _.set(filters, fieldToSet, newCount);
  }

  /**
   * Returns the tracker from filters for a given project and tracker ID.
   * @param {any} filters - The filters object.
   * @param {string} project_id - The project ID.
   * @param {string} tracker_id - The tracker ID.
   * @returns {any} The tracker object.
   */
  getTrackerFromFilters(filters: any, project_id: string, tracker_id: string): any {
    const project = _.find(filters.trackers.data, { id: project_id.toString() });
    return _.find(_.get(project, 'trackers', {}), { id: tracker_id.toString() });
  }

  /**
   * Toggles the selected status of a tracker, updates counts, and enables related elements.
   * @param {any} filters - The filters object.
   * @param {string} project_id - The project ID.
   * @param {string} tracker_id - The tracker ID.
   */
  toggleSelectedTracker(filters: any, project_id: string, tracker_id: string): void {
    const tracker = this.getTrackerFromFilters(filters, project_id, tracker_id);
    if (_.isEmpty(tracker)) {
      return;
    }
    // Invert selected status.
    tracker.selected = !tracker.selected;
    const trackerIds = this.getSelectedTrackerIds(filters.trackers.data);
    filters.trackers.count = _.size(trackerIds);
    // Update parent project selection based on trackers.
    _.forEach(filters.trackers.data, (p: any) => {
      p.selected = (_.filter(p.trackers, 'selected').length > 0);
    });
    // Update enabled elements.
    this.setFilterEnabledElements(filters.assignees.data, trackerIds);
    this.setFilterEnabledElements(filters.statuses.data, trackerIds);
  }

  /**
   * Counts the selected trackers across projects.
   * @param {any} trackersByProject - The trackers grouped by project.
   * @returns {number} The count of selected trackers.
   */
  countSelectedTrackers(trackersByProject: any): number {
    return _.sum(_.map(trackersByProject.data, (project: any) => {
      return _.sum(_.map(project.trackers, (tracker: any) => tracker.selected ? 1 : 0));
    }));
  }

  /**
   * Processes tracker extra fields and sets up filter elements accordingly.
   * @param {any} filters - The filters object.
   * @param {any} tefs - Tracker extra fields.
   * @returns {any} The updated filters object.
   */
  processTrackerExtraFields(filters: any, tefs: any): any {
    _.forOwn(tefs, (tef, alias) => {
      if (!tef.isExtraField) {
        return;
      }
      // Set the output field.
      filters.displayFields.extra.push({ label: tef.fieldName, type: tef.fieldType, selected: false });
      // Set filter elements based on field type.
      switch (tef.fieldType) {
        case 1:
        case 2:
        case 3:
        case 5:
        case 8:
        case 12:
          filters.selects.extra.push({
            id: tef.id,
            alias: alias,
            label: tef.fieldName,
            type: tef.fieldType,
            elements: _.map(tef.fieldElements, (element: any) => {
              return { id: element.id, alias: element.label, label: element.label, selected: false };
            })
          });
          break;
        case 4:
        case 6:
          filters.text.extra.push({
            id: tef.id,
            alias: alias,
            label: tef.fieldName,
            type: tef.fieldType,
            value: ""
          });
          break;
        case 10:
        case 11:
          filters.numerics.extra.push({
            id: tef.id,
            alias: alias,
            label: tef.fieldName,
            type: tef.fieldType,
            from: "",
            to: ""
          });
          break;
        case 9:
          filters.dates.extra.push({
            id: tef.id,
            alias: alias,
            label: tef.fieldName,
            type: tef.fieldType,
            from: "",
            to: ""
          });
          break;
      }
    });
    return filters;
  }

  /**
   * Sets the tracker extra fields in the filters by fetching them using TrackerRest.
   * @param {any} filters - The filters object.
   * @param {string} tracker_id - The tracker ID.
   * @returns {Promise<any>} A promise resolving to the updated filters.
   */
  setTrackerExtraFields(filters: any, tracker_id: any): Promise<any> {
    this.removeTrackerBuiltInFields(filters);
    if (!tracker_id) {
      filters.displayFields.extra = [];
      filters.selects.extra = [];
      filters.text.extra = [];
      filters.dates.extra = [];
      filters.numerics.extra = [];
      return Promise.resolve(filters);
    }
    return this.trackerRest.details(tracker_id, {rel: 'fields'})
      .then((response: any) => {
        this.setTrackerBuiltInFields(filters, response);
        return this.processTrackerExtraFields(filters, response.fields);
      })
      .catch(() => null);
  }

  private setTrackerBuiltInFields(filters: any, tracker: any) {
    //Enable tracker-specific fields
    if (this.trackerService.isSupportTracker(tracker)) {
        filters.displayFields.basic.push({ label: 'Waiting For', type: 110, selected: false });
        filters.selects.basic.push({ alias: 'waitingFor', elements: this.trackerService.fields.builtIn.waiting_for.getList(), label: 'Waiting For', id: 0, type: 110, selected: false, isIntrinsic: true });
    }
}

  private removeTrackerBuiltInFields(filters) {
    _.remove(filters.displayFields.basic, function (item: any) {
        return item.type == 110;
    });

    _.remove(filters.selects.basic, function (item: any) {
        return item.alias == 'waitingFor';
    });
  }


  /**
   * Maps report settings to the filters object.
   * @param {any} filters - The filters object.
   * @param {any} reportCriteria - The report configuration.
   * @returns {Promise<any>} A promise resolving to the updated filters.
   */
  mapReportSettingsToFilters(filters: any, reportCriteria: any): Promise<any> {
    // Map basic criteria.
    filters.basic.reportName = reportCriteria.reportName;
    filters.basic.isShared = reportCriteria.isShared;
    filters.basic.openDateStart = reportCriteria.openDateStart;
    filters.basic.openDateEnd = reportCriteria.openDateEnd;
    filters.basic.closeDateStart = reportCriteria.closeDateStart;
    filters.basic.closeDateEnd = reportCriteria.closeDateEnd;
    filters.basic.sharedUsers = _.join(
      _.map(reportCriteria.sharedUsers, (sharedUser: any) => {
        return sharedUser.unixName;
      }),
      ', '
    );

    // Set trackers.
    let trackerIds = _.map(_.get(reportCriteria, 'trackers', []), (tracker: any) => {
      return { project_id: tracker?.project?.id, tracker_id: tracker.id };
    });
    _.forEach(trackerIds, (item) => {
      this.toggleSelectedTracker(filters, item.project_id, item.tracker_id);
    });
    trackerIds = this.getSelectedTrackerIds(filters.trackers.data);
    let promise: Promise<any>;
    if (trackerIds.length === 1) {
      promise = this.setTrackerExtraFields(filters, trackerIds[0]);
    } else {
      promise = Promise.resolve();
    }
    return promise.then(() => {
      // Set display fields.
      _.forEach(reportCriteria.displayFields, (field: any) => {
        let item;
        if (field.type >= 100 && field.type <= 110) {
          item = _.find(filters.displayFields.basic, { type: field.type });
        } else {
          item = _.find(filters.displayFields.extra, { label: field.fieldName });
        }
        if (item) {
          item.selected = true;
          item.columnOrder = field.columnOrder;
        }
      });
      // Set display fields count.
      filters.displayFields.count = this.countSelectedElements(filters.displayFields.basic) + this.countSelectedElements(filters.displayFields.extra);

      // Preserves tracker-specific statuses, fallbacks to base (intrinsic) statuses only when none are available
      if (_.isEmpty(filters.statuses.data)) {
        const trackerIdsCopy = _.clone(trackerIds);
        filters.statuses.data = _.map(this.getDefaultStatuses(), status =>
          Object.assign({}, status, { trackers: trackerIdsCopy })
        );
      }

      // Set other field filters.
      _.forEach(reportCriteria.fieldFilters, (filterItem: any) => {
        let groupId: string, subgroupId: string;
        groupId = _.get(this.fieldTypeToFilterGroup, filterItem.type.toString(), '');
        if (_.includes(['selects', 'text', 'dates', 'numerics'], groupId)) {
          subgroupId = filterItem.fieldName;
        }
        if (_.includes(['selects', 'statuses', 'assignees', 'priorities'], groupId)) {
          _.forEach(filterItem.values, (valueItem: any) => {
            this.toggleSelectedElement(filters, groupId, subgroupId, valueItem.value);
          });
        } else {
          let filter;
          if (filterItem.type >= 101 && filterItem.type <= 110) {
            filter = _.find(_.union(_.get(filters, groupId + '.basic', []), _.get(filters, groupId + '.extra', [])), { type: filterItem.type });
          } else {
            filter = _.find(_.union(_.get(filters, groupId + '.basic', []), _.get(filters, groupId + '.extra', [])), { label: subgroupId });
          }
          const filterValue = (filterItem.values.length > 0) ? filterItem.values[0].value : "";
          if (filter) {
            switch (groupId) {
              case 'text':
                filter.value = filterValue;
                break;
              case 'dates':
              case 'numerics':
                const arrValue = _.split(filterValue, '|');
                if (arrValue.length === 2) {
                  filter.from = arrValue[0];
                  filter.to = arrValue[1];
                }
                break;
            }
          }
        }
      });
      return filters;
    });
  }

  /**
   * Maps the assignees filter to a field filter.
   * @param {any} assigneesFilter - The assignees filter.
   * @returns {any[]} Array of field filter objects.
   */
  mapAssigneesFilterToFieldFilter(assigneesFilter: any): any[] {
    const values = _.filter(assigneesFilter.data, { selected: true });
    if (values.length > 0) {
      return [{
        fieldName: "tracker_item_assignee",
        type: 103,
        values: _.map(values, (value) => ({ value: value.id }))
      }];
    } else {
      return [];
    }
  }

  /**
   * Maps the statuses filter to a field filter.
   * @param {any} statusesFilter - The statuses filter.
   * @returns {any[]} Array of field filter objects.
   */
  mapStatusesFilterToFieldFilter(statusesFilter: any): any[] {
    const values = _.filter(statusesFilter.data, { selected: true });
    if (values.length > 0) {
      return [{
        fieldName: "Status",
        type: 7,
        values: _.map(values, (value) => ({ value: value.label }))
      }];
    } else {
      return [];
    }
  }

  /**
   * Maps the priorities filter to a field filter.
   * @param {any} prioritiesFilter - The priorities filter.
   * @returns {any[]} Array of field filter objects.
   */
  mapPrioritiesFilterToFieldFilter(prioritiesFilter: any): any[] {
    const values = _.filter(prioritiesFilter.data, { selected: true });
    if (values.length > 0) {
      return [{
        fieldName: "tracker_item.PRIORITY",
        type: 104,
        values: _.map(values, (value) => ({ value: value.id }))
      }];
    } else {
      return [];
    }
  }

  /**
   * Maps text filters to field filters.
   * @param {any} textFilters - The text filters.
   * @returns {any[]} Array of field filter objects.
   */
  mapTextFiltersToFieldFilter(textFilters: any): any[] {
    const filtersArr = _.union(textFilters.basic, textFilters.extra);
    const ret: any[] = [];
    _.forEach(filtersArr, (filter: any) => {
      if (filter.value !== "") {
        let fieldName = "";
        if (_.get(filter, 'type', 0) === 101 || _.get(filter, 'type', 0) === 102) {
          fieldName = this.fieldTypeToReportFieldLabel[filter.type];
        } else {
          fieldName = filter.label;
        }
        ret.push({
          fieldName: fieldName,
          type: filter.type,
          values: [{ value: filter.value }]
        });
      }
    });
    return ret;
  }

  /**
   * Maps range filters (dates or numerics) to field filters.
   * @param {any} rangeFilters - The range filters.
   * @returns {any[]} Array of field filter objects.
   */
  mapRangeFiltersToFieldFilter(rangeFilters: any): any[] {
    const filtersArr = _.union(rangeFilters.basic, rangeFilters.extra);
    const ret: any[] = [];
    filtersArr.forEach((filter: any) => {
      if (filter.from !== "" && filter.to !== "") {
        let fieldName = "";
        if ([105, 106, 107].includes(_.get(filter, 'type', 0))) {
          fieldName = this.fieldTypeToReportFieldLabel[filter.type];
        } else {
          fieldName = filter.label;
        }
        ret.push({
          fieldName: fieldName,
          type: filter.type,
          values: [{ value: filter.from + "|" + filter.to }]
        });
      }
    });
    return ret;
  }

  /**
   * Maps select filters to field filters.
   * @param {any} selectFilters - The select filters.
   * @returns {any[]} Array of field filter objects.
   */
  mapSelectFiltersToFieldFilter(selectFilters: any): any[] {
    const filtersArr = _.concat(selectFilters.basic, selectFilters.extra);
    const ret: any[] = [];
    filtersArr.forEach((filter: any) => {
      const elements = _.filter(_.get(filter, 'elements', []), { selected: true });
      if (elements.length > 0) {
        ret.push({
          fieldName: filter.label,
          type: filter.type,
          values: elements.map((element: any) => {
            return { value: (filter.type < 100) ? element.label : element.id };
          })
        });
      }
    });
    return ret;
  }


  /**
 * Maps the given filters to the report settings object.
 * This method takes the current filters object and applies its values to update the provided report object.
 * 
 * @param myReport - The report object to be updated.
 * @param filters - The filters object containing the current filter settings.
 * @returns The updated report object.
 */
mapFiltersToReportSettings(myReport: any, filters: any): any {
  myReport.projects = [];
  myReport.trackers = [];
  myReport.displayFields = [];
  myReport.fieldFilters = [];

  // Map basic criteria.
  myReport.reportName = filters.basic.reportName;
  myReport.isShared = filters.basic.isShared;

  // Map basic dates.
  myReport.openDateStart = filters.basic.openDateStart;
  myReport.openDateEnd = filters.basic.openDateEnd;
  myReport.closeDateStart = filters.basic.closeDateStart;
  myReport.closeDateEnd = filters.basic.closeDateEnd;

  // Map shared users.
  myReport.sharedUsers = _.split(filters.basic.sharedUsers, ',')
    .map((item: string) => {
      return (_.trim(item) !== "") ? { unixName: _.trim(item) } : null;
    })
    .filter((item: any) => (item != null));

  if (myReport.reportType === 1) {
    myReport.trackers = [];
  } else {
    // Map projects and trackers.
    _.forEach(filters.trackers.data, (project: any) => {
      const selectedTrackers = _.map(
        _.filter(project.trackers, { selected: true }),
        (tracker: any) => ({ id: tracker.id })
      );

      // If report type is ALL trackers, do not send the trackers list
      // as it will be dynamically calculated on the backend.
      myReport.trackers = _.union(myReport.trackers, selectedTrackers);

      if (selectedTrackers.length > 0) {
        myReport.projects.push({ id: project.id });
      }
    });
  }

  myReport.projects = _.uniqBy(myReport.projects, 'id');
  myReport.trackers = _.uniqBy(myReport.trackers, 'id');

  // Map displayFields.
  myReport.displayFields = _.map(
    _.filter(
      _.union(filters.displayFields.basic, filters.displayFields.extra),
      { selected: true }
    ),
    (item: any) => {
      return {
        fieldName: (item.type >= 101 && item.type <= 109)
          ? this.fieldTypeToReportFieldLabel[item.type]
          : item.label,
        type: item.type ? item.type : 0,
        columnOrder: item.columnOrder ? item.columnOrder : 0
      };
    }
  );

  // Map field filters.
  // - Map assignees.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapAssigneesFilterToFieldFilter(filters.assignees)
  );

  // - Map statuses.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapStatusesFilterToFieldFilter(filters.statuses)
  );

  // - Map priorities.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapPrioritiesFilterToFieldFilter(filters.priorities)
  );

  // - Map text filters.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapTextFiltersToFieldFilter(filters.text)
  );

  // - Map date filters.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapRangeFiltersToFieldFilter(filters.dates)
  );

  // - Map numeric filters.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapRangeFiltersToFieldFilter(filters.numerics)
  );

  // - Map select filters.
  myReport.fieldFilters = _.union(
    myReport.fieldFilters,
    this.mapSelectFiltersToFieldFilter(filters.selects)
  );

  return myReport;
}

}
